import { Injectable } from '@angular/core';

// Session-only undo/redo + version history for the AI writing-assist fields
// (Question / Notes). Lives here rather than on the step object because the
// roadmap is re-fetched and re-rendered after every save, which replaces the
// step objects — history keyed in this root service survives that teardown, the
// same way the component's focus-restoration keys do. Nothing is persisted: the
// map is dropped when the app session ends, matching the product decision that
// history resets on reload.

export type AiAction = 'original' | 'proofread' | 'rewrite' | 'generate' | 'adjust';

export interface HistoryEntry {
  text: string;
  action: AiAction;
  at: number;
  // 'generate': the description the draft came from.
  // 'adjust': the instruction the consultant gave. Version history shows it, so
  // a result can be read back against what was actually asked for.
  prompt?: string;
}

export interface FieldHistory {
  entries: HistoryEntry[];
  index: number; // points at the current entry
}

@Injectable({
  providedIn: 'root',
})
export class AiWritingHistoryService {
  // Key = `${subSectionKey}::${stepKey}::${field}` (built by the component from
  // the same stable identifiers it uses for focus restoration).
  private store = new Map<string, FieldHistory>();

  // Seeds the field's current text as the 'original' entry the first time an AI
  // op touches it. No-op once history exists, so repeated ops never re-seed and
  // wipe the stack.
  ensureSeed(key: string, text: string): void {
    if (this.store.has(key)) return;
    this.store.set(key, {
      entries: [{ text: text ?? '', action: 'original', at: Date.now() }],
      index: 0,
    });
  }

  // Pushes an AI result as the new current entry, dropping any redo tail first —
  // a fresh action after an undo forks the timeline (standard undo/redo rule).
  record(key: string, entry: HistoryEntry): void {
    const h = this.store.get(key);
    if (!h) return;
    h.entries = h.entries.slice(0, h.index + 1);
    h.entries.push(entry);
    h.index = h.entries.length - 1;
  }

  // Folds the field's live text into the current entry without creating a new
  // step. Called just before undo/redo/restore so manual typing since the last
  // AI op is preserved (redo returns to it) but never becomes its own undo step.
  syncCurrent(key: string, text: string): void {
    const h = this.store.get(key);
    const current = h?.entries[h.index];
    if (current) current.text = text ?? '';
  }

  undo(key: string): HistoryEntry | null {
    const h = this.store.get(key);
    if (!h || h.index <= 0) return null;
    h.index--;
    return h.entries[h.index];
  }

  redo(key: string): HistoryEntry | null {
    const h = this.store.get(key);
    if (!h || h.index >= h.entries.length - 1) return null;
    h.index++;
    return h.entries[h.index];
  }

  restoreTo(key: string, index: number): HistoryEntry | null {
    const h = this.store.get(key);
    if (!h || index < 0 || index >= h.entries.length) return null;
    h.index = index;
    return h.entries[index];
  }

  canUndo(key: string): boolean {
    const h = this.store.get(key);
    return !!h && h.index > 0;
  }

  canRedo(key: string): boolean {
    const h = this.store.get(key);
    return !!h && h.index < h.entries.length - 1;
  }

  // True once there's more than the seeded original — i.e. at least one AI op
  // succeeded, so the undo/redo/history controls have something to act on.
  hasHistory(key: string): boolean {
    const h = this.store.get(key);
    return !!h && h.entries.length > 1;
  }

  get(key: string): FieldHistory | null {
    return this.store.get(key) ?? null;
  }

  clear(key: string): void {
    this.store.delete(key);
  }
}
